import dayjs, { UnitTypeLong } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import isEmpty from 'lodash/isEmpty';

export type Requirement = 'all' | 'optional';
export type Unit = UnitTypeLong | 'week' | 'quarter';

dayjs.extend(relativeTime);

/**
 * Calculates the elapsed time between two dates with options for a detailed or simple relative format.
 *
 * @param {Object} params - The parameters for calculating elapsed time.
 * @param {string} params.date - The start date in string format.
 * @param {null | string} [params.compareDate=null] - The date to compare with; defaults to the current date if not provided.
 * @param {boolean} params.detailed - If `true`, returns the time difference in the specified unit; if `false`, returns a simple relative time string.
 * @param {Requirement} params.requirement - Determines whether both `date` and `compareDate` are required (`all`) or just `date` (`optional`).
 * @param {Unit} params.unit - The unit of time (e.g., "second", "minute") to use if `detailed` is `true`.
 * @returns {string | number | null} - Returns a relative time string if `detailed` is `false`, a number representing the difference in units if `detailed` is `true`, or `null` if validation fails.
 *
 * @example
 * // Returns relative time as a string (e.g., "2 days ago")
 * getElapsedTime({ date: '2023-01-01', detailed: false, requirement: 'optional', unit: 'day' });
 *
 * @example
 * // Returns time difference in specified unit (e.g., 48 if `unit` is 'hour')
 * getElapsedTime({ date: '2023-01-01', compareDate: '2023-01-03', detailed: true, requirement: 'all', unit: 'hour' });
 */
const getElapsedTime = ({
  date,
  compareDate = null,
  detailed = false,
  requirement,
  unit,
}: {
  date: string;
  compareDate: null | string;
  detailed: boolean;
  requirement: Requirement;
  unit: Unit;
}) => {
  if (requirement === 'all' && (isEmpty(date) || isEmpty(compareDate)))
    return null;
  if (requirement === 'optional' && isEmpty(date)) return null;

  const startDate = dayjs(date).set('second', 0).set('millisecond', 0);
  const endDate = (compareDate ? dayjs(compareDate) : dayjs())
    .set('second', 0)
    .set('millisecond', 0);

  if (!startDate.isValid()) {
    console.error('Invalid start date provided', startDate);
    return null;
  }

  if (compareDate && !endDate.isValid()) {
    console.error('Invalid comparison date provided', endDate);
    return null;
  }

  // Return simple relative time if detailed is false
  if (!detailed) return startDate.from(endDate);

  return endDate.diff(startDate, unit);
};

export default getElapsedTime;
